import React, { useState, useEffect, useRef, useContext } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { store } from "../context/store";
import Header from "../components/Header";
import LeftMenu from "../components/LeftMenu";
import TimetableBarChart from "../components/TimetableBarChart";
import { functions } from "../constants/common-functions";

export default function Timetable() {
  const globalState = useContext(store);

  const HISTOGRAM_URL = "/svg/histogram.svg";

  const [svgUrl, setSvgUrl] = useState(HISTOGRAM_URL);
  const [chartData, setChartData] = useState([]);
  const [svgContent, setSvgContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [title, setTitle] = useState("");
  const [displayTitle, setDisplayTitle] = useState("none");
  const [selectedBand, setSelectedBand] = useState(null);
  const [weekCode, setWeekCode] = useState(null);

  const scrollContainerRef = useRef(null);

  const hideProgress = () => setLoading(false);

  // --- Load static SVG ---
  useEffect(() => {
    setLoading(true);
    fetch(svgUrl)
      .then((res) => res.text())
      .then((text) => {
        setSvgContent(text);
        hideProgress();
        if (svgUrl.includes("histogram")) setZoom(6);
      })
      .catch(() => hideProgress());
  }, [svgUrl]);

  // --- Initial chart data (year summary) ---
  useEffect(() => {
    loadInitialChartData();
  }, []);

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const loadInitialChartData = () => {
    functions.getArrayFromAjax(
      "/resolutionsoftwareplatform/dashboard/timetable/json/sizeband/year",
      null,
      populateChartData
    );
  };

  const loadWeekChartData = (weekCode) => {
    functions.getArrayFromAjax(
      "/resolutionsoftwareplatform/dashboard/timetable/json/sizeband/week?code=" +
        weekCode,
      null,
      populateChartData
    );
  };

  const loadSizeBandDailyChartData = (weekCode, bandCode) => {
    functions.getArrayFromAjax(
      "/resolutionsoftwareplatform/dashboard/timetable/json/sizeband/daily?code=" +
        weekCode +
        "&band=" +
        bandCode,
      null,
      populateChartDataDaily
    );
  };

  const populateChartData = (results) => {
    setChartData(
      results.map((r) => ({
        id: r.id,
        label: r.name,
        count: r.value,
      }))
    );
  };

  const populateChartDataDaily = (results) => {
    setChartData(
      results.map((r) => ({
        id: r.id,
        label: dayLabels[parseInt(r.name)] || r.name,
        count: r.value,
      }))
    );
  };

  // --- Scroll to bottom for histogram ---
  useEffect(() => {
    if (scrollContainerRef.current && svgUrl.includes("histogram")) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [svgContent, svgUrl]);

  useEffect(() => {
    setDisplayTitle(title && title.length > 0 ? "flex" : "none");
  }, [title]);

  // --- WEEK CHANGE HANDLER ---
  const setWeek = (code) => {
    const cleanWeek = code.replace(/\D/g, "");
    setTitle(cleanWeek);
    setWeekCode(cleanWeek);
    setZoom(1);
    setLoading(true);
    setSvgUrl(`svg/week-${cleanWeek}.svg`);
    loadWeekChartData(cleanWeek);
  };

  // expose for onclick in SVG
  useEffect(() => {
    window.setWeek = setWeek;
  }, []);

  // --- BACK TO YEAR ---
  const handleBackToYear = () => {
    setLoading(true);
    setTimeout(() => {
      setSvgUrl(HISTOGRAM_URL);
      setZoom(1);
      setTitle("");
      setSelectedBand(null);
      setWeekCode(null);
      loadInitialChartData();
    }, 800);
  };

  // --- BAR CLICK HANDLER ---
  const handleBarClick = (bandLabel) => {
    if (!weekCode) return;
    setSelectedBand(bandLabel);
    loadSizeBandDailyChartData(weekCode, bandLabel);
  };

  // --- BACK TO WEEK ---
  const handleBackToWeek = () => {
    if (!weekCode) return;
-   loadWeekChartData(weekCode);
+   loadWeekChartData(weekCode);
+   setSelectedBand(null); // hide "Back to Week" button after returning
  };

  // --- Zoom ---
  const zoomIn = () =>
    setZoom((z) => Math.min(z + 0.5, svgUrl.includes("histogram") ? 6 : 8));
  const zoomOut = () =>
    setZoom((z) =>
      svgUrl.includes("histogram")
        ? Math.max(z - 0.5, 0.5)
        : Math.max(z - 0.5, 1)
    );

  // --- STYLES ---
  const container = {
    display: "flex",
    position: "fixed",
    flexDirection: "column",
    width: "100%",
    height: "100vh",
    left: "0px",
    top: "0px",
  };

  const main = {
    display: "flex",
    position: "fixed",
    top: "80px",
    left: "60px",
    bottom: "0px",
    right: "0px",
    overflow: "hidden",
  };

  const svgPanel = {
    flex: 1,
    overflow: "hidden",
    borderRight: "1px solid #ccc",
    position: "relative",
  };

  const chartPanel = {
  	display:'flex',
  	flexDirection:"column",
    width: "400px",
    borderLeft: "1px solid #ccc",
    padding: "10px",
    overflowY: "auto",
    backgroundColor: "#fff",
  };

  const titleBox = {
    display: displayTitle,
    position: "fixed",
    left: "176px",
    top: "90px",
    backgroundColor: "#f4f5f7",
    height: "32px",
    paddingLeft: "20px",
    color: "black",
    fontSize: "14px",
    alignItems: "center",
    borderTopRightRadius: "10px",
    borderBottomRightRadius: "10px",
    borderBottom: "1px solid #dcdfe3",
    width: "200px",
    zIndex: "3000",
  };

  const backButton = {
    position: "fixed",
    top: "90px",
    left: "70px",
    zIndex: 10,
    padding: "6px 12px",
    backgroundColor: "#333",
    color: "#fff",
    border: "none",
    fontSize: "14px",
    cursor: "pointer",
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
  };

  return (
    <div style={container}>
      <Header />
      <LeftMenu />

      {!svgUrl.includes("histogram") && (
        <button onClick={handleBackToYear} style={backButton}>
           Back to Year
        </button>
      )}

      {/* Zoom Controls */}
      <div
        style={{
          position: "absolute",
          top: "90px",
          right: "470px",
          zIndex: 10,
          display: "flex",
          gap: "5px",
        }}
      >
        <button
          onClick={zoomOut}
          style={{
            padding: "6px 10px",
            backgroundColor: "#333",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          
        </button>
        <span
          style={{
            padding: "6px 10px",
            backgroundColor: "#555",
            color: "#fff",
            borderRadius: "4px",
            minWidth: "40px",
            textAlign: "center",
          }}
        >
          {zoom.toFixed(1)}x
        </span>
        <button
          onClick={zoomIn}
          style={{
            padding: "6px 10px",
            backgroundColor: "#333",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>

      <div style={main}>
        {/* SVG viewer */}
        <div style={svgPanel}>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.6)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <CircularProgress style={{ color: "#fff", marginBottom: "10px" }} />
              <span style={{ color: "#fff", fontSize: "16px" }}>
                Loading timetable
              </span>
            </div>
          )}

          {svgContent && (
            <div
              ref={scrollContainerRef}
              style={{
                width: "100%",
                height: "100%",
                overflow: "auto",
                background: "#fff",
              }}
            >
              <div
                style={{
                  display: svgUrl.includes("histogram") ? "block" : "inline-block",
                  transform: `scale(${zoom})`,
                  transformOrigin: "0 0",
                  margin: svgUrl.includes("histogram") ? "0 auto" : "0",
                }}
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            </div>
          )}
        </div>

        {/* Chart panel */}
        <div style={chartPanel}>
          {selectedBand && (
            <button
              onClick={handleBackToWeek}
              style={{
                marginBottom: "10px",
                backgroundColor: "#333",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "6px 12px",
                cursor: "pointer",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
               Back to Week
            </button>
          )}

          <h3
            style={{
              textAlign: "center",
              marginTop: "10px",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {selectedBand
              ? `Week ${weekCode}  ${selectedBand} Capacity (by Day)`
              : weekCode
              ? `Week ${weekCode}  Usage by Room Size`
              : "Usage by Room Size"}
          </h3>

          <div
            id="chartContainer"
            style={{
              width: "100%",
              height: "calc(100% - 60px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
            }}
          >
            <TimetableBarChart
              chartId="timetablebar"
              chartData={chartData}
              categoryKey="label"
              valueKey="count"
              width="100%"
              height="100%"
              onClick={handleBarClick}
            />
          </div>
        </div>
      </div>

      <div style={titleBox}>{weekCode && `F${weekCode}`}</div>
    </div>
  );
}



